// Product Zoom Handler
class ProductZoomHandler {
  constructor() {
    this.zoomModal = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupZoomHandlers());
    } else {
      this.setupZoomHandlers();
    }
  }

  setupZoomHandlers() {
    // Find the zoom modal
    this.zoomModal = document.querySelector('product-zoom-modal');
    
    if (!this.zoomModal) {
      console.warn('Product zoom modal not found');
      return;
    }

    // Setup zoom triggers for all product images
    this.setupImageZoomTriggers();
    
    // Setup direct image click handlers
    this.setupDirectImageClicks();
  }

  setupImageZoomTriggers() {
    // Only setup zoom functionality on mobile devices
    if (!this.isMobile()) {
      return;
    }

    // Handle zoom triggers from product-media.liquid (main product images)
    const zoomTriggers = document.querySelectorAll('.product-media-zoom-trigger');
    
    zoomTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const wrapper = trigger.closest('.product-media-image-wrapper');
        if (wrapper) {
          const mediaId = wrapper.dataset.mediaId;
          const image = wrapper.querySelector('.product-media-image');
          
          if (image && mediaId) {
            this.openZoomModal(image, mediaId);
          }
        }
      });
    });

    // Handle zoom triggers from product-thumbnail.liquid (thumbnail images)
    const thumbnailZoomWrappers = document.querySelectorAll('.product-zoom-trigger-wrapper');
    
    thumbnailZoomWrappers.forEach(wrapper => {
      wrapper.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const mediaId = wrapper.dataset.mediaId;
        const image = wrapper.querySelector('.product-zoom-image');
        
        if (image && mediaId) {
          this.openZoomModal(image, mediaId);
        }
      });
    });
  }

  setupDirectImageClicks() {
    // Only setup zoom functionality on mobile devices
    if (!this.isMobile()) {
      return;
    }

    // Handle direct clicks on main product images
    const productImages = document.querySelectorAll('.product-media-image');
    
    productImages.forEach(image => {
      // Add click handler for direct image clicks
      image.addEventListener('click', (e) => {
        // Only handle if not clicking on zoom trigger
        if (e.target.closest('.product-media-zoom-trigger')) {
          return;
        }
        
        const wrapper = image.closest('.product-media-image-wrapper');
        if (wrapper) {
          const mediaId = wrapper.dataset.mediaId;
          this.openZoomModal(image, mediaId);
        }
      });
    });

    // Handle direct clicks on thumbnail images
    const thumbnailImages = document.querySelectorAll('.product-zoom-image');
    
    thumbnailImages.forEach(image => {
      image.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const wrapper = image.closest('.product-zoom-trigger-wrapper');
        if (wrapper) {
          const mediaId = wrapper.dataset.mediaId;
          this.openZoomModal(image, mediaId);
        }
      });
    });
  }

  isMobile() {
    return window.innerWidth <= 768 || 'ontouchstart' in window;
  }

  openZoomModal(image, mediaId) {
    if (!this.zoomModal) return;

    // Check if this is a video or image
    const wrapper = image.closest('.product-zoom-trigger-wrapper');
    const isVideo = wrapper && wrapper.classList.contains('product__modal-opener--video') || 
                   wrapper && wrapper.classList.contains('product__modal-opener--external_video');

    if (isVideo) {
      this.openVideoModal(image, mediaId);
    } else {
      this.openImageModal(image, mediaId);
    }
  }

  openImageModal(image, mediaId) {
    // Get image data
    const src = image.dataset.zoomSrc || image.src;
    const alt = image.alt;
    
    // Hide video container, show image container
    const videoContainer = this.zoomModal.querySelector('.product-zoom-modal__video-container');
    const imageContainer = this.zoomModal.querySelector('.product-zoom-modal__image-container');
    
    if (videoContainer) videoContainer.style.display = 'none';
    if (imageContainer) imageContainer.style.display = 'flex';
    
    // Update modal image
    const modalImage = this.zoomModal.querySelector('.product-zoom-modal__image');
    if (modalImage) {
      modalImage.src = src;
      modalImage.alt = alt;
      modalImage.dataset.mediaId = mediaId;
    }

    // Show the modal
    this.zoomModal.show(mediaId);
  }

  openVideoModal(image, mediaId) {
    // Get video data from the original media
    const wrapper = image.closest('.product-zoom-trigger-wrapper');
    const mediaType = wrapper.dataset.mediaType || (wrapper.classList.contains('product__modal-opener--video') ? 'video' : 'external_video');
    
    // Hide image container, show video container
    const imageContainer = this.zoomModal.querySelector('.product-zoom-modal__image-container');
    const videoContainer = this.zoomModal.querySelector('.product-zoom-modal__video-container');
    
    if (imageContainer) imageContainer.style.display = 'none';
    if (videoContainer) videoContainer.style.display = 'flex'; 
    
    // Clear any existing video content
    const videoContainerContent = this.zoomModal.querySelector('.product-zoom-modal__video-content');
    if (videoContainerContent) {
      videoContainerContent.innerHTML = '';
    }
    
    // Get video source from data attributes - prioritize HD source
    let videoSrc = wrapper.dataset.videoSrcHd || wrapper.dataset.videoSrc;
    const externalVideoUrl = wrapper.dataset.externalVideoUrl;
    
    // If no video source found, try to get it from the original media element
    if (!videoSrc && mediaType === 'video') {
      // Try to find the original video element in the default modal
      const originalVideo = document.querySelector(`[data-media-id="${mediaId}"] video`);
      if (originalVideo) {
        const source = originalVideo.querySelector('source');
        if (source) {
          videoSrc = source.src;
        }
      }
      
      // If still no source, try to get it from the media element template
      if (!videoSrc) {
        const mediaElement = document.querySelector(`[data-media-id="${mediaId}"]`);
        if (mediaElement) {
          const template = mediaElement.querySelector('template');
          if (template) {
            const templateContent = template.content;
            const videoInTemplate = templateContent.querySelector('video');
            if (videoInTemplate) {
              const sourceInTemplate = videoInTemplate.querySelector('source');
              if (sourceInTemplate) {
                videoSrc = sourceInTemplate.src;
              }
            }
          }
        }
      }
    }
    
    // Debug: Log video source information
    if (mediaType === 'video') {
            
      // Test if video URL is accessible
      if (videoSrc) {
        fetch(videoSrc, { method: 'HEAD' })
          .then(response => {
                     })
          .catch(error => {
            console.error('Video URL test failed:', {
              url: videoSrc,
              error: error.message
            });
          });
      }
    }
    
    // Create the appropriate video element based on media type
    this.createVideoElement(mediaId, mediaType, image.src, videoSrc, externalVideoUrl);

    // Show the modal
    this.zoomModal.show(mediaId);
  }

  createVideoElement(mediaId, mediaType, posterSrc, videoSrc, externalVideoUrl) {
    const videoContainer = this.zoomModal.querySelector('.product-zoom-modal__video-container');
    if (!videoContainer) return;

    // Create a container for the video content
    let videoContent = videoContainer.querySelector('.product-zoom-modal__video-content');
    if (!videoContent) {
      videoContent = document.createElement('div');
      videoContent.className = 'product-zoom-modal__video-content';
      videoContainer.appendChild(videoContent);
    }

    if (mediaType === 'external_video') {
      // Handle external videos (YouTube, Vimeo, etc.)
      this.createExternalVideoElement(videoContent, mediaId, posterSrc, externalVideoUrl);
    } else {
      // Handle regular video files
      this.createRegularVideoElement(videoContent, mediaId, posterSrc, videoSrc);
    }
  }

  createExternalVideoElement(container, mediaId, posterSrc, externalVideoUrl) {
    if (!externalVideoUrl) {
      // Fallback if no external video URL is provided
      container.innerHTML = `
        <div style="
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          color: #666;
          text-align: center;
          padding: 20px;
        ">
          <div style="font-size: 48px; margin-bottom: 16px;">🎥</div>
          <div style="font-size: 18px; margin-bottom: 8px;">External Video</div>
          <div style="font-size: 14px;">Video URL not available</div>
        </div>
      `;
      return;
    }

    // Create an iframe for external videos
    container.innerHTML = `
      <iframe
        src="${externalVideoUrl}"
        style="
          width: 100%;
          height: 100%;
          border: none;
          object-fit: cover;
          background: #fff;
        "
        allow="autoplay; encrypted-media; fullscreen"
        allowfullscreen
        title="Product Video"
      ></iframe>
    `;
  }

  createRegularVideoElement(container, mediaId, posterSrc, videoSrc) {
    if (!videoSrc) {
      // Fallback if no video source is provided
      container.innerHTML = `
        <div style="
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          color: #666;
          text-align: center;
          padding: 20px;
        ">
          <div style="font-size: 48px; margin-bottom: 16px;">🎥</div>
          <div style="font-size: 18px; margin-bottom: 8px;">Video Playback</div>
          <div style="font-size: 14px;">Video source not available</div>
        </div>
      `;
      return;
    }

    // Determine MIME type based on file extension
    const getMimeType = (url) => {
      const extension = url.split('.').pop().toLowerCase();
      switch (extension) {
        case 'mp4': return 'video/mp4';
        case 'webm': return 'video/webm';
        case 'ogg': return 'video/ogg';
        case 'ogv': return 'video/ogg';
        case 'avi': return 'video/avi';
        case 'mov': return 'video/quicktime';
        default: return 'video/mp4'; // Default fallback
      }
    };

    const mimeType = getMimeType(videoSrc);

    // For regular video files, create a proper video element with multiple source formats
    container.innerHTML = `
      <video
        class="product-zoom-modal__video"
        controls
        preload="auto"
        poster="${posterSrc}"
        style="
          width: 100%;
          height: 100%;
          object-fit: cover;
          background: #fff;
        "
        data-media-id="${mediaId}"
        autoplay
        muted
      >
        <source src="${videoSrc}" type="${mimeType}">
        <source src="${videoSrc}" type="video/mp4">
        <source src="${videoSrc}" type="video/webm">
        <source src="${videoSrc}" type="video/ogg">
        <div style="
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #000;
          color: white;
          font-size: 16px;
          text-align: center;
          padding: 20px;
          box-sizing: border-box;
        ">
          Your browser does not support the video tag.
        </div>
      </video>
    `;

    // Add comprehensive error handling and loading monitoring
    const video = container.querySelector('.product-zoom-modal__video');
    if (video) {
      let loadTimeout;
      let hasLoaded = false;
      
      // Set a timeout for video loading
      loadTimeout = setTimeout(() => {
        if (!hasLoaded) {
          console.warn('Video loading timeout after 10 seconds');
          container.innerHTML = `
            <div style="
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-direction: column;
              color: #666;
              text-align: center;
              padding: 20px;
            ">
              <div style="font-size: 48px; margin-bottom: 16px;">⏱️</div>
              <div style="font-size: 18px; margin-bottom: 8px;">Video Loading Timeout</div>
              <div style="font-size: 14px;">Video is taking too long to load</div>
              <div style="font-size: 12px; margin-top: 8px; color: #999;">Source: ${videoSrc}</div>
              <button onclick="location.reload()" style="
                margin-top: 16px;
                padding: 8px 16px;
                background: #007bff;
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
              ">Retry</button>
            </div>
          `;
        }
      }, 10000); // 10 second timeout
      
      // Monitor video loading events
      // video.addEventListener('loadstart', () => {
      //   console.log('Video load started:', videoSrc);
      // });
      
      video.addEventListener('loadedmetadata', () => {
        hasLoaded = true;
        clearTimeout(loadTimeout);
        
        // Try to play the video automatically
        video.play().catch(e => {
          console.log('Autoplay prevented, user interaction required');
        });
      });
      
      video.addEventListener('canplay', () => {
        hasLoaded = true;
        clearTimeout(loadTimeout);
        
        // Ensure video is ready to play
        if (video.paused) {
          video.play().catch(e => {
            console.log('Autoplay prevented, user interaction required');
          });
        }
      });
      
      video.addEventListener('loadeddata', () => {
        hasLoaded = true;
        clearTimeout(loadTimeout);
      });
      
      video.addEventListener('error', (e) => {
        console.error('Video load error:', e, video.error);
        hasLoaded = true;
        clearTimeout(loadTimeout);
        
        // Show detailed error information
        const errorCode = video.error ? video.error.code : 'unknown';
        const errorMessage = video.error ? video.error.message : 'Unknown error';
        
        container.innerHTML = `
          <div style="
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            color: #666;
            text-align: center;
            padding: 20px;
          ">
            <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
            <div style="font-size: 18px; margin-bottom: 8px;">Video Load Error</div>
            <div style="font-size: 14px;">Could not load video</div>
            <div style="font-size: 12px; margin-top: 8px; color: #999;">Source: ${videoSrc}</div>
            <div style="font-size: 12px; color: #999;">MIME: ${mimeType}</div>
            <div style="font-size: 12px; color: #999;">Error Code: ${errorCode}</div>
            <div style="font-size: 12px; color: #999;">Error: ${errorMessage}</div>
            <button onclick="window.open('${videoSrc}', '_blank')" style="
              margin-top: 16px;
              padding: 8px 16px;
              background: #28a745;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
            ">Test Video URL</button>
          </div>
        `;
      });
    }
  }

}

// Initialize the zoom handler
if (!window.ProductZoomHandler) {
  window.ProductZoomHandler = new ProductZoomHandler();
}